import cloneDeep from 'lodash/cloneDeep';
import { imageKit } from '@meitu/wasm-lib';
import {
    EdgelessObject,
    normalizeResultScale,
    type IEdgelessPreviewClearable,
    type IFramework,
    type IGetSrcImage,
    type IResultOptions,
    type ISetResource,
} from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { isEmptyEffect } from '@/utils/layer-kit';
import { rotatePoint } from '@/utils/math-kit';
import { LayerType, type IImageLayerProps, type ILayerProps } from '@/version-types';
import { getImageEffectResultImgSize } from './plugins/image-effect/runtime/constants';
import type { IImageEffectCanvasResult } from './plugins/image-effect/runtime/image-effect-action';
import { ImageEffectOutput } from './render/effect-output';
import {
    getImageLayerEffectArtifact,
    getImageLayerEffectPosition,
    loadBaseImage,
    renderImageLayerCanvas,
    type IImageEffectRenderArtifact,
    type IRenderImageLayerCanvasOptions,
} from './render/render-to-canvas';

type IImageLayerResourceOptions = Omit<
    IRenderImageLayerCanvasOptions,
    'effectCode' | 'setEffectCode' | 'reportImageError'
>;

export type IImageCanvasResultOptions = Omit<IImageLayerResourceOptions, 'scale'> & {
    aiWatermark?: boolean;
};

export class EdgelessImageLayer
    extends EdgelessObject
    implements IGetSrcImage, ISetResource, IEdgelessPreviewClearable
{
    static type = LayerType.IMAGE_LAYER;

    private effectCode: string | null = null;
    private currentUrl: string | null = null;
    private currentBaseImage: HTMLCanvasElement | null = null;
    /** 底图请求代次，避免旧请求回写已切换或重新提交的图片缓存 */
    private baseImageLoadVersion = 0;
    /** 滤镜预览时的临时底图（调色 GL 进行中不生效） */
    private previewBaseImage: HTMLCanvasElement | null = null;
    /** 插件本地草稿；只参与当前对象渲染，不写入 flatLayer、历史或协同 */
    private previewLayer: ILayerProps | null = null;
    /** 当前图片基础特效 GL 预览会话 token */
    private effectPreviewToken: symbol | null = null;
    /** 本地预览渲染代次，防止快速调节时旧异步结果覆盖最新结果 */
    private previewRenderRevision = 0;
    /** 源图 GL 调参时复用的已提交基础特效 canvas，避免反复加载 base64 派生图 */
    private persistedEffectUnderlay: { key: string; canvas: HTMLCanvasElement } | null = null;
    /** 正在加载的基础特效派生图；快速连续调参共享同一个图片请求 */
    private persistedEffectUnderlayTask: { key: string; task: Promise<HTMLCanvasElement | undefined> } | null = null;
    private isDisposed = false;
    private readonly effectOutput = new ImageEffectOutput();
    /** 已由编辑操作启动的底图预览任务；保存只等待，不启动特效。 */
    private pendingEffectPreview: Promise<HTMLCanvasElement> | null = null;
    private pendingEffectRender: Promise<HTMLCanvasElement> | null = null;
    private effectRenderRevision = 0;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
    }

    private getCurrentUrl() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer) return null;
        return (layer.data as IImageLayerProps).url;
    }

    private checkDirty() {
        return this.getCurrentUrl() !== this.currentUrl;
    }

    private async loadImage() {
        const url = this.getCurrentUrl();
        const version = ++this.baseImageLoadVersion;
        this.currentUrl = url;
        this.currentBaseImage = null;
        this.previewBaseImage = null;
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const baseImage =
            layer && url ? await loadBaseImage(layer, this.service.reportImageError.bind(this.service)) : null;
        if (this.isDisposed || version !== this.baseImageLoadVersion || url !== this.getCurrentUrl()) {
            return false;
        }
        this.currentBaseImage = baseImage;
        return true;
    }

    getPreviewBaseImage(): HTMLCanvasElement | null {
        return this.previewBaseImage;
    }

    /**
     * 投影当前操作已经完成的特效结果
     * @param layer - 保存或下载使用的图层副本
     * @returns 是否存在与该副本输入匹配的结果
     * 副作用：仅写入副本；等待已经启动的预览任务，不触发渲染
     */
    async projectOutputEffect(layer: ILayerProps) {
        await this.pendingEffectPreview?.catch(() => undefined);
        await this.pendingEffectRender?.catch(() => undefined);
        return !this.isDisposed && this.effectOutput.project(layer);
    }

    /** 获取当前正式或草稿图层的特效局部边界。 */
    getEffectPosition() {
        const layer = this.previewLayer ?? this.service.flatLayer.getLayer(this.layerId);
        return layer ? getImageLayerEffectPosition(layer) : undefined;
    }

    /**
     * 使用插件草稿触发统一图片图层渲染
     *
     * @param layer - 与当前对象同 ID 的本地草稿
     * @param token - 可选；图片基础特效预览会话 token
     * @returns 本次草稿仍为最新版本时生成的画布，否则返回 null
     */
    async setPreviewLayer(layer: ILayerProps, token?: symbol) {
        if (layer.id !== this.layerId || this.isDisposed) return null;
        this.effectPreviewToken = token ?? null;
        const revision = ++this.previewRenderRevision;
        const previewLayer = cloneDeep(layer);
        this.previewLayer = previewLayer;
        const cachedBaseImage =
            (previewLayer.data as IImageLayerProps).url === this.currentUrl ? this.currentBaseImage : null;
        const baseImage =
            cachedBaseImage ?? (await loadBaseImage(previewLayer, this.service.reportImageError.bind(this.service)));
        let artifact: IImageEffectRenderArtifact | undefined;
        let nextEffectCode = this.effectCode;
        const canvas = await renderImageLayerCanvas(previewLayer, {
            baseImage,
            globalAlpha: false,
            scale: this.getDisplayScale(),
            effectCode: this.effectCode,
            effectArtifact: this.effectOutput.getArtifact(previewLayer),
            onEffectArtifact: (result) => {
                artifact = result;
            },
            setEffectCode: (effectCode) => {
                nextEffectCode = effectCode;
            },
            reportImageError: this.service.reportImageError.bind(this.service),
        });
        if (
            revision !== this.previewRenderRevision ||
            (token !== undefined && token !== this.effectPreviewToken) ||
            this.isDisposed
        ) {
            return null;
        }
        if (artifact) this.effectOutput.retain(previewLayer, artifact);
        this.effectCode = nextEffectCode;
        this.resource = canvas;
        this.syncPreviewBaseInfo(previewLayer);
        this.notifyCompositeParent(false);
        this.service.edgelessCanvas.setDirty({ object: this });
        return canvas;
    }

    /**
     * 使用基础特效 GL canvas 渲染插件草稿
     *
     * @param token - 图片特效预览会话 token
     * @param layer - 与当前对象同 ID 的本地草稿
     * @param effectUnderlayCanvas - complexBridge 的独立 canvas 快照
     * @returns 本次草稿仍为最新版本时生成的画布，否则返回 null
     */
    async setEffectPreviewLayer(token: symbol, layer: ILayerProps, effectUnderlayCanvas: HTMLCanvasElement) {
        if (layer.id !== this.layerId || this.isDisposed) return null;
        this.effectPreviewToken = token;
        const revision = ++this.previewRenderRevision;
        const previewLayer = cloneDeep(layer);
        this.previewLayer = previewLayer;
        const cachedBaseImage =
            (previewLayer.data as IImageLayerProps).url === this.currentUrl ? this.currentBaseImage : null;
        const baseImage =
            cachedBaseImage ?? (await loadBaseImage(previewLayer, this.service.reportImageError.bind(this.service)));
        const canvas = await renderImageLayerCanvas(previewLayer, {
            baseImage,
            effectUnderlayCanvas,
            globalAlpha: false,
            scale: this.getDisplayScale(),
            reportImageError: this.service.reportImageError.bind(this.service),
        });
        if (revision !== this.previewRenderRevision || token !== this.effectPreviewToken || this.isDisposed) {
            return null;
        }
        this.effectOutput.retainPreview(previewLayer, {
            canvas: effectUnderlayCanvas,
            effectPosition: cloneDeep((previewLayer.data as IImageLayerProps).effect?.effectPosition),
        });
        this.resource = canvas;
        this.syncPreviewBaseInfo(previewLayer);
        this.notifyCompositeParent(false);
        this.service.edgelessCanvas.setDirty({ object: this });
        return canvas;
    }

    /**
     * 清理指定图片基础特效 GL 预览会话
     *
     * @param token - 要清理的预览会话 token
     * @param rerender - 是否立即按正式图层重新渲染，默认 true
     * @returns token 是否匹配并完成清理
     */
    clearEffectPreviewLayer(token: symbol, rerender = true) {
        if (this.effectPreviewToken !== token) return false;
        this.effectPreviewToken = null;
        this.clearPreviewLayer(rerender);
        return true;
    }

    /**
     * 清除插件草稿
     *
     * @param rerender - 是否立即按最新正式图层重新渲染，默认 true
     */
    clearPreviewLayer(rerender = true) {
        this.previewRenderRevision += 1;
        this.previewLayer = null;
        this.effectPreviewToken = null;
        this.effectOutput.clearPreview();
        if (rerender && !this.isDisposed) void this.update(false);
    }

    async getSrcImage() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer) {
            throw new Error('===>未找到对应的图片图层');
        }
        const { url } = layer.data as IImageLayerProps;
        await base64Kit.getRemoteUrlAsync(url);
        return loadImageAsync(url);
    }

    private notifyCompositeParent(onlyUpdateBaseInfo?: boolean) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer || this.isDisposed) return;
        if (!this.service.flatLayer.checkWrapIsGroupExtension(layer)) return;

        const parentObj = this.service.edgelessCanvas.getByLayerId(layer.parentId!);
        if (parentObj) {
            if (onlyUpdateBaseInfo) {
                void parentObj.update(true);
            } else {
                this.service.edgelessCanvas.requestObjectResourceUpdate(parentObj);
            }
        }
    }

    /**
     * 更新图片并通知父级拼图同步几何或合并资源刷新
     *
     * @param onlyUpdateBaseInfo - 是否仅更新位置、尺寸等基础属性
     * @param isAdd - 是否首次插入图层
     * @returns 更新调度完成的 Promise，不代表异步资源加载完成
     */
    override async update(onlyUpdateBaseInfo: boolean, isAdd = false) {
        const updating = super.update(onlyUpdateBaseInfo, isAdd);
        // 在调度器继续消费父图层前合并通知，避免微任务再次把本帧已加载的父图层入队。
        this.notifyCompositeParent(onlyUpdateBaseInfo);
        return updating;
    }

    private async getPersistedEffectUnderlay(layer: ILayerProps) {
        if (isEmptyEffect(layer.data as IImageLayerProps)) return undefined;
        const effect = (layer.data as IImageLayerProps).effect;
        const artifact = getImageLayerEffectArtifact(layer);
        if (effect?.effectPosition && !getImageLayerEffectPosition(layer)) return undefined;
        const screenshotUrl = artifact?.screenshotUrl || effect?.screenshotUrl;
        if (!screenshotUrl) return undefined;

        const effectPosition = getImageLayerEffectPosition(layer);
        const key = `${screenshotUrl}:${JSON.stringify(effectPosition)}`;
        if (this.persistedEffectUnderlay?.key === key) {
            return this.persistedEffectUnderlay.canvas;
        }
        if (this.persistedEffectUnderlayTask?.key === key) {
            return this.persistedEffectUnderlayTask.task;
        }

        const task = loadImageAsync(base64Kit.getLocalUrl(screenshotUrl))
            .then((image) => createCanvas(image.width, image.height, image).canvas)
            .catch(() => undefined);
        this.persistedEffectUnderlayTask = { key, task };
        const canvas = await task;
        const isCurrentTask = this.persistedEffectUnderlayTask?.task === task;
        if (isCurrentTask) {
            this.persistedEffectUnderlayTask = null;
        }
        if (canvas && isCurrentTask && !this.isDisposed) {
            this.persistedEffectUnderlay = { key, canvas };
        }
        return canvas;
    }

    async setResource(baseImage: HTMLImageElement | HTMLCanvasElement) {
        const layer = cloneDeep(this.service.flatLayer.getLayer(this.layerId));
        if (!layer || this.isDisposed) return;
        const revision = ++this.previewRenderRevision;

        const baseCanvas =
            baseImage instanceof HTMLCanvasElement
                ? baseImage
                : createCanvas(baseImage.width, baseImage.height, baseImage).canvas;

        this.previewBaseImage = baseCanvas;
        this.notifyCompositeParent(false);

        let preview: IImageEffectCanvasResult | undefined;
        const task = renderImageLayerCanvas(layer, {
            baseImage: baseCanvas,
            noEffect: isEmptyEffect(layer.data as IImageLayerProps),
            effectCachePolicy: 'preview-rebuild',
            onEffectPreview: (result) => {
                preview = result;
            },
            globalAlpha: false,
            scale: this.getDisplayScale(),
            reportImageError: this.service.reportImageError.bind(this.service),
        });
        this.pendingEffectPreview = task;
        let canvas: HTMLCanvasElement;
        try {
            canvas = await task;
        } finally {
            if (this.pendingEffectPreview === task) this.pendingEffectPreview = null;
        }
        if (revision !== this.previewRenderRevision || this.isDisposed) return;
        if (preview) this.effectOutput.retainPreview(layer, preview, baseCanvas);
        this.resource = canvas;
        this.service.edgelessCanvas.setDirty({ object: this });
    }

    /**
     * 清除图片本体和图层草稿的本地预览资源
     *
     * @param options - 可选；`rerender` 控制清理后是否立即按正式图层重新渲染，默认 true
     */
    clearPreviewResource({ rerender = true }: { rerender?: boolean } = {}) {
        this.previewBaseImage = null;
        this.clearPreviewLayer(rerender);
    }

    /** 落盘后清除预览与 url 缓存，强制按最新图层数据重载 */
    commitAppliedResource() {
        this.baseImageLoadVersion += 1;
        this.invalidatePendingResource();
        this.previewBaseImage = null;
        this.previewLayer = null;
        this.previewRenderRevision += 1;
        this.currentUrl = null;
        this.currentBaseImage = null;
        this.persistedEffectUnderlay = null;
        this.persistedEffectUnderlayTask = null;
    }

    private getCachedBaseImage() {
        if (this.getCurrentUrl() === this.currentUrl && this.currentBaseImage) {
            return this.currentBaseImage;
        }
        return undefined;
    }

    private getDisplayScale() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer) return 1;
        const retinaScaling = this.service.edgelessCanvas.retinaScaling;
        return super.getResourceScale({
            naturalWidth: layer.width * retinaScaling,
            naturalHeight: layer.height * retinaScaling,
        });
    }

    private getLayerBaseInfo(layer: ILayerProps) {
        const size = { width: layer.width, height: layer.height };
        return {
            ...this.calcCenterIsLeftTopPosition({
                left: layer.left,
                top: layer.top,
                rotate: layer.rotate,
                ...size,
            }),
            ...size,
        };
    }

    private syncPreviewBaseInfo(layer: ILayerProps) {
        this.visible = !this.service.flatLayer.checkIsNeedHidden(layer);
        this.opacity = layer.opacity;
        this.angle = layer.rotate;
        const baseInfo = this.getBaseInfo();
        this.left = baseInfo.left;
        this.top = baseInfo.top;
        this.width = baseInfo.width;
        this.height = baseInfo.height;
        this.setCoords();
    }

    private getScreenResourceOptions(options: IImageLayerResourceOptions = {}): IImageLayerResourceOptions {
        return {
            ...options,
            scale: options.scale ?? this.getDisplayScale(),
        };
    }

    private async getResource(options: IImageLayerResourceOptions = {}) {
        const layer = cloneDeep(this.previewLayer ?? this.service.flatLayer.getLayer(this.layerId));
        if (!layer || this.isDisposed) return null;
        const glCanvas = glKit.isUseGL(layer) ? (imageKit.getGLCanvas() as HTMLCanvasElement) : undefined;
        const cachedBaseImage = this.getCachedBaseImage();
        // GL 结果有两种语义：普通 GL 处理替换图片本体；图片特效处理则是图层下方的描边/阴影等效果层。
        // 因此 __isImageEffectProcessed 成立时仍用原图渲染图片本体，把 GL canvas 作为 underlay 单独合成。
        const isEffectUnderlayProcessed = layer.__isImageEffectProcessed;
        const resolvedBaseImage = isEffectUnderlayProcessed ? cachedBaseImage : glCanvas || cachedBaseImage;
        const isSourceGLPreview = Boolean(glCanvas && !isEffectUnderlayProcessed);
        const persistedEffectUnderlay = isSourceGLPreview ? await this.getPersistedEffectUnderlay(layer) : undefined;

        const revision = ++this.effectRenderRevision;
        const task = renderImageLayerCanvas(layer, {
            ...options,
            baseImage: resolvedBaseImage,
            effectUnderlayCanvas: isEffectUnderlayProcessed ? glCanvas : persistedEffectUnderlay,
            noEffect: options.noEffect || layer.__isBasicOperating || (isSourceGLPreview && !persistedEffectUnderlay),
            effectCode: this.effectCode,
            effectArtifact: this.effectOutput.getArtifact(layer),
            onEffectArtifact: (artifact) => {
                if (revision === this.effectRenderRevision && !this.isDisposed)
                    this.effectOutput.retain(layer, artifact);
                options.onEffectArtifact?.(artifact);
            },
            setEffectCode: (effectCode) => {
                if (revision === this.effectRenderRevision) this.effectCode = effectCode;
            },
            reportImageError: this.service.reportImageError.bind(this.service),
        });
        this.pendingEffectRender = task;
        try {
            return await task;
        } finally {
            if (this.pendingEffectRender === task) this.pendingEffectRender = null;
        }
    }

    private async createResultCanvas({
        aiWatermark = false,
        ...options
    }: IImageCanvasResultOptions & { scale?: number }) {
        const canvas = await this.getResource(options);
        if (!canvas) return null;
        if (aiWatermark) {
            const layer = this.service.flatLayer.getLayer(this.layerId);
            if (!layer || this.isDisposed) return null;
            const emptyEffect = isEmptyEffect(layer.data as IImageLayerProps);
            await super.drawAiWatermarkAsync(canvas, undefined, !emptyEffect && !options.noEffect);
        }
        return canvas;
    }

    /** 获取图片图层专用 Canvas 结果。 */
    async getCanvasResult(args: IImageCanvasResultOptions = {}) {
        return this.createResultCanvas(args);
    }

    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer || this.isDisposed) return {};
        const resultScale = isEmptyEffect(layer.data as IImageLayerProps) ? normalizeResultScale(scale) : 1;
        const canvas = await this.createResultCanvas({
            aiWatermark,
            scale: resultScale,
        });
        // 特效渲染会生成最新 effectPosition；必须在 canvas 完成后读取同代边界。
        const renderBox = this.getBaseInfo();
        return canvas ? { canvas, renderBox } : {};
    }

    override loadResource() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer || this.isDisposed) return null;
        const emptyEffect = isEmptyEffect(layer.data as IImageLayerProps);
        if (this.checkDirty()) {
            return this.loadImage().then((isCurrent) => {
                if (!isCurrent) return undefined;
                return this.getResource(this.getScreenResourceOptions({ globalAlpha: false })).then((canvas) => {
                    if (!canvas || this.isDisposed) return null;
                    return super.drawAiWatermarkAsync(canvas, undefined, !emptyEffect).then(() => canvas);
                });
            });
        }

        return this.getResource(this.getScreenResourceOptions({ globalAlpha: false })).then((canvas) => {
            if (!canvas || this.isDisposed) return null;
            super.drawAiWatermark(canvas, undefined, !emptyEffect);
            return canvas;
        });
    }

    override render(ctx: CanvasRenderingContext2D) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer || this.isDisposed) return;
        if (layer.__isCropImage || layer.__isDeformationImage) return;

        if (this.service.flatLayer.checkWrapIsGroupExtension(layer)) {
            const parentObj = this.service.edgelessCanvas.getByLayerId(layer.parentId!);
            if (parentObj) {
                return;
            }
        }
        super.render(ctx);
    }

    override dispose() {
        this.isDisposed = true;
        this.baseImageLoadVersion += 1;
        this.invalidatePendingResource();
        this.previewRenderRevision += 1;
        this.previewLayer = null;
        this.effectPreviewToken = null;
        this.currentUrl = null;
        this.currentBaseImage = null;
        this.previewBaseImage = null;
        this.effectOutput.clearPreview();
        this.pendingEffectPreview = null;
        this.pendingEffectRender = null;
        this.persistedEffectUnderlay = null;
        this.persistedEffectUnderlayTask = null;
        super.dispose();
    }

    override getBaseInfo() {
        const layer = this.previewLayer ?? this.service.flatLayer.getLayer(this.layerId);
        if (!layer || this.isDisposed) {
            return {
                left: this.left,
                top: this.top,
                width: this.width,
                height: this.height,
            };
        }

        const baseLayout = this.getLayerBaseInfo(layer);

        const { width: layerWidth, height: layerHeight } = layer;

        if (layer.__isBasicOperating || isEmptyEffect(layer.data as IImageLayerProps)) {
            return baseLayout;
        }

        const effectPosition = getImageLayerEffectPosition(layer);
        if (effectPosition) {
            const width = effectPosition.right - effectPosition.left;
            const height = effectPosition.bottom - effectPosition.top;
            const leftTop = rotatePoint(
                {
                    x: effectPosition.left - layerWidth / 2,
                    y: effectPosition.top - layerHeight / 2,
                },
                layer.rotate,
            );

            return {
                left: leftTop.x + layer.left + layerWidth / 2,
                top: leftTop.y + layer.top + layerHeight / 2,
                width,
                height,
            };
        }

        const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);

        const width = layerWidth * imgEffectResultImgSize;
        const height = layerHeight * imgEffectResultImgSize;
        const rotate = layer.rotate;

        const leftTop = rotatePoint({ x: -width / 2, y: -height / 2 }, rotate);

        return {
            left: leftTop.x + layer.left + layerWidth / 2,
            top: leftTop.y + layer.top + layerHeight / 2,
            width,
            height,
        };
    }
}
